import React from "react";
import * as Sentry from "@sentry/react";
import { Result, Spin } from "antd";
import { Button } from "../../design-system";
import {
    CHUNK_RECOVERY_RELOAD_DELAY_MS,
    attemptChunkRecoveryReload,
    isCSSChunkLoadError,
    isLazyDefaultExportError,
    isViteModuleImportError,
    isVitePreloadCancellation,
} from "../../utils/chunkRecovery";

const isRecoverableChunkError = (error) => (
    error?.name === "ChunkLoadError" ||
    /Loading chunk/.test(error?.message || "") ||
    isCSSChunkLoadError(error) ||
    isViteModuleImportError(error) ||
    isLazyDefaultExportError(error) ||
    isVitePreloadCancellation(error)
);

export class ChunkErrorBoundary extends React.Component {
    state = {
        hasError: false,
        isRecoveringFromChunkError: false,
        resetKey: this.props.resetKey,
    };

    static getDerivedStateFromProps(props, state) {
        if (props.resetKey === state.resetKey) {
            return null;
        }

        return {
            hasError: false,
            isRecoveringFromChunkError: false,
            resetKey: props.resetKey,
        };
    }

    static getDerivedStateFromError(error) {
        return {
            hasError: true,
            isRecoveringFromChunkError: isRecoverableChunkError(error),
        };
    }

    componentDidCatch(error, errorInfo) {
        if (isRecoverableChunkError(error)) {
            const scheduledReload = attemptChunkRecoveryReload(
                { message: error?.message, error },
                { reloadDelayMs: CHUNK_RECOVERY_RELOAD_DELAY_MS }
            );

            if (!scheduledReload) {
                this.setState({ isRecoveringFromChunkError: false });
            }
            return;
        }

        Sentry.captureException(error, { extra: errorInfo });
    }

    render() {
        if (!this.state.hasError) {
            return this.props.children;
        }

        if (this.state.isRecoveringFromChunkError) {
            return (
                <Result
                    status="info"
                    title="There is a newer version of HRP Platform"
                    subTitle="Reloading in 2 seconds..."
                    extra={<Spin aria-label="Reloading newer version" />}
                />
            );
        }

        return (
            <Result
                status="error"
                title="Failed to load page"
                extra={
                    <>
                        <Button onClick={() => window.location.reload()}>
                            Reload
                        </Button>
                        <a
                            href="/reset.html"
                            style={{
                                color: "#1B1B1B",
                                display: "inline-block",
                                marginLeft: 12,
                                textDecoration: "underline",
                            }}
                        >
                            Reset HRP Session
                        </a>
                    </>
                }
            />
        );
    }
}

export default ChunkErrorBoundary;
